<template>
  <div class="thomsonspecialmachining-spl thomson-app">
    
    <!-- Partnumber input for testing -->
    <form v-if="globalSettings.showTestPartnumberInput" class="test-partnumber-form" style="display: flex; margin-bottom: 15px;" action="#" @submit.prevent="submitParserField()">
      <label style="margin: auto 10px auto 0;">Enter Part Number:</label>
      <input type="text" v-model="partnumber" style="flex-grow: 1;">
      <button type="submit" class="button button--xs">Submit</button>
    </form>

    <template v-if="parsedPartnumberOutput">

      <div class="machining-options-container" :class="machiningOptionsContainerClass">
        <div class="machining-options__header-container">
          <span class="machining-options__header">{{machiningOptionsHeader}}</span>
          <span v-if="!isDistributor" class="machining-options__header-tagline"><a :href="customizationUrl" target="_blank">{{$t('having-trouble')}}</a> <span class="glyphicon glyphicon-comment"></span></span>
        </div>

        <div class="machining-options">
          <div v-for="option in machiningOptions" :key="option.name" class="machining-option" :class="{'machining-option--applied': option.isApplied, 'machining-option--disabled': !option.isAvailable}" @click="option.isAvailable ? machiningOptionClicked(option) : null">
            <div>
              <img class="machining-option__image" :class="{'flip-image': selectedSection == 'Left'}" :src="option.cardImage">
            </div>
            <span class="machining-option__name">{{option.displayName}}</span>
          </div>
        </div>
      </div>

      <div class="machining-details">
        <div class="machining-tabs">
          <div class="machining-tab machining-tab--left" :class="{'machining-tab--selected': selectedSection == 'Left', 'machining-tab--disabled': !validSections.includes('Left')}" @click="machiningSectionClicked('Left')"><span>{{$t('left-end-machining')}}</span></div>
          <div class="machining-tab machining-tab--radial" :class="{'machining-tab--selected': selectedSection == 'Radial', 'machining-tab--disabled': !validSections.includes('Radial')}" @click="machiningSectionClicked('Radial')"><span>{{$t('center-machining')}}</span></div>
          <div class="machining-tab machining-tab--right" :class="{'machining-tab--selected': selectedSection == 'Right', 'machining-tab--disabled': !validSections.includes('Right')}" @click="machiningSectionClicked('Right')"><span>{{$t('right-end-machining')}}</span></div>
        </div>

        <div class="machining-details__image-container">

          <div class="machining-details__image-color-backgrounds">
            <div class="machining-details__image-color-background machining-details__image-color-background--left" :class="{'machining-details__image-color-background--active': selectedSection == 'Left'}"></div>
            <div class="machining-details__image-color-background machining-details__image-color-background--radial" :class="{'machining-details__image-color-background--active': selectedSection == 'Radial'}"></div>
            <div class="machining-details__image-color-background machining-details__image-color-background--right" :class="{'machining-details__image-color-background--active': selectedSection == 'Right'}"></div>
          </div>

          <div class="machining-details__images">
            <img :src="machiningDetailsLeftImage" :style="`margin-top:${infoBoxesContainerHeight}px`">
            <img :src="machiningDetailsRadialImage" :style="`margin-top:${infoBoxesContainerHeight}px`">
            <img :src="machiningDetailsRightImage" :style="`margin-top:${infoBoxesContainerHeight}px`">
          </div>

          <div class="machining-details__image-touch-targets">
            <div class="machining-details__image-touch-target" :class="{'machining-details__image-touch-target--disabled': !validSections.includes('Left')}" @click="machiningSectionClicked('Left')"></div>
            <div class="machining-details__image-touch-target" :class="{'machining-details__image-touch-target--disabled': !validSections.includes('Radial')}" @click="machiningSectionClicked('Radial')"></div>
            <div class="machining-details__image-touch-target" :class="{'machining-details__image-touch-target--disabled': !validSections.includes('Right')}" @click="machiningSectionClicked('Right')"></div>
          </div>

          <div class="machining-details__info-boxes-container" ref="infoBoxesContainer">
            <div v-for="detail in parsedPartnumberOutput.machiningDetails" :key="detail.area" class="machining-details__info-boxes">
              <div v-for="option in detail.area == 'Left' ? appliedLeftOptions : detail.area == 'Radial' ? appliedRadialOptions : appliedRightOptions" :key="option.name" class="machining-details__info-box">
                <span class="machining-details__info-box-title">{{option.displayName}}</span>
                <div class="machining-details__info-box-details">
                  <div class="machining-details__info-box-image-container">
                    <img class="machining-details__info-box-image" :class="{'flip-image': detail.area == 'Left'}" :src="option.cardImage">
                  </div>
                  <div class="machining-details__info-box-buttons buttons">
                    <button class="button button--xs" @click="editMachiningButtonClicked(detail.area, option)">{{$t('edit')}}</button>
                    <button class="button button--xs" @click="deleteMachiningButtonClicked(detail.area, option)">{{$t('delete')}}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="machining-details__image-tagline">
            <span>{{$t('image-not-to-scale')}}</span>
          </div>

          <div class="machining-details__configured-option-tags-container">
            <div v-for="detail in parsedPartnumberOutput.machiningDetails" :key="detail.area" class="machining-details__configured-option-tags">
              <div v-for="(tags, index) in detail.area == 'Left' ? appliedLeftOptionsTags : detail.area == 'Radial' ? appliedRadialOptionsTags : appliedRightOptionsTags" :key="index" class="machining-details__configured-option-tag-group">
                <template v-for="(tag, index) in tags" :key="index">
                  <span class="machining-details__configured-option-tag"><strong>{{tag.key}}:</strong> {{tag.value}}</span>
                </template>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div class="footer">
        <div class="buttons">
          <button class="button button--md button--full-width button--secondary" :class="{'button--disabled': !partnumberHasChanged}" @click="partnumberHasChanged ? resetButtonClicked() : null">{{$t('reset')}}</button>
          <button class="button button--md button--full-width button--secondary" @click="cancelButtonClicked">{{$t('cancel')}}</button>
          <button class="button button--md button--full-width" :class="{'button--disabled': !partnumberHasChanged}" @click="partnumberHasChanged ? saveButtonClicked() : null">{{$t('save')}}</button>
        </div>
      </div>

      <div class="overlay" v-if="showLoader"></div>
      <div v-if="showLoader" class="loading-spinner">
        <half-circle-spinner
          :animation-duration="1000"
          :size="60"
          color="black"
        />
      </div>

    </template>
    <template v-else>
      <div v-if="showLoader" class="initial-spinner">
        <half-circle-spinner
          :animation-duration="1000"
          :size="60"
          color="black"
        />
      </div>
      <div v-else-if="showTimError" class="tooltip-popup__container">
        <div id="service-error-popup" class="tooltip-popup tooltip-popup--error">
          <span class="tooltip-popup__title">{{$t('alert')}}</span>
          <p>{{ $t('error.tim-service')}}</p>
        </div>
      </div>
    </template>

    
    <sweet-modal @close="clearSelectedOption()" overlay-theme="dark" class="machining-option-modal" ref="modalMachiningOption" :title="machiningOptionModalHeader">
      <MachiningOption 
        v-if="selectedOption"
        ref="machiningOption"
        :option="selectedOption"
        @cancelButtonClicked="closeMachiningOptionModal"
        @saveButtonClicked="refreshPartnumber"
        @openHelpModal="openHelpModal"
      />
    </sweet-modal>
    
    <sweet-modal overlay-theme="dark" class="machining-option-variable-help-modal" ref="modalMachiningOptionVariableHelp" :title="machiningOptionVariableHelpModalHeader">
      <div v-if="helpModalVariable" v-html="helpModalVariable.helpHMTL"></div>
    </sweet-modal>

  </div>
</template>

<script>

// Components
import MachiningOption from "./components/MachiningOption.vue"

// Mixins
import locale from "./mixins/locale"

// Helpers
import "../node_modules/glyphicons-only-bootstrap/css/bootstrap.min.css"
import { HalfCircleSpinner } from "epic-spinners";

import tim from "./services/tim.js";

export default {
  name: 'App',
  components: {
    MachiningOption,
    HalfCircleSpinner,
  },
  mixins: [locale],
  data: () => {
    return {
      publicPath: process.env.BASE_URL,
      tloUrl: process.env.VUE_APP_TLO_URL,
      globalSettings: window.thomsonSettings,
      isDistributor: process.env.VUE_APP_TYPE === "distributor",

      infoBoxesContainerHeight: 234,

      initialPartnumber: null,
      partnumber: null,
      parsedPartnumberOutput: null,
      
      showLoader: false,
      showTimError: false,

      selectedSection: "Left",
      validSections: ['Left','Right'], // Hardcoding Radial as an invalid section for now. Can be made dynamic by checking which sections have available options
      selectedOption: null,

      helpModalVariable: null,
    }
  },
  mounted() {
    this.updateInfoBoxesContainerHeight();
    window.addEventListener("resize", this.updateInfoBoxesContainerHeight, { passive: true });

    let _this = this;
    document.addEventListener('splSpecialMachiningInit', function(e) {
      _this.init(e.detail);
    });
    
    if (this.globalSettings && this.globalSettings.splSpecialMachiningPartnumber) {
      this.init(this.globalSettings.splSpecialMachiningPartnumber);
    }
  },
  computed: {
    
    customizationUrl() { return this.tloUrl + this.$t('path.tlo.ball-spline-customization') },

    machiningOptionsContainerClass() {
      switch (this.selectedSection) {
        case "Left":
          return "machining-options-container--left";
        case "Radial":
          return "machining-options-container--radial";
        case "Right":
          return "machining-options-container--right";
      }
    },
    machiningOptionsHeader() {
      switch (this.selectedSection) {
        case "Left":
          return this.$t('left-end-machining');
        case "Radial":
          return this.$t('center-machining');
        case "Right":
          return this.$t('right-end-machining');
      }
    },
    machiningOptions() { return this.parsedPartnumberOutput.machiningDetails.find(x => x.area == this.selectedSection).machiningOptions; },

    machiningDetailsLeftImage() { return this.parsedPartnumberOutput.machiningDetails.find(x => x.area == "Left").image; },
    machiningDetailsRadialImage() { return this.parsedPartnumberOutput.machiningDetails.find(x => x.area == "Radial").image; },
    machiningDetailsRightImage() { return this.parsedPartnumberOutput.machiningDetails.find(x => x.area == "Right").image; },

    appliedLeftOptions() { return this.parsedPartnumberOutput.machiningDetails.find(x => x.area == 'Left').machiningOptions.filter(option => option.isApplied == true); },
    appliedRadialOptions() { return this.parsedPartnumberOutput.machiningDetails.find(x => x.area == 'Radial').machiningOptions.filter(option => option.isApplied == true); },
    appliedRightOptions() { return this.parsedPartnumberOutput.machiningDetails.find(x => x.area == 'Right').machiningOptions.filter(option => option.isApplied == true); },

    globalTags() {
      let tags = [];

      this.parsedPartnumberOutput.globalMachiningVariables.forEach(variable => {
        if (variable.visible == true) {
          tags.push({key: variable.code, value: variable.value + ' ' + variable.uom});
        }
      });

      return tags;
    },
    appliedLeftOptionsTags() {
      let tags = [];

      this.appliedLeftOptions.forEach(option => {
        let optionTags = [];
        option.variables.forEach(variable => {
          if (variable.isDropDown) {
            let option = variable.options.find(x => x.code == variable.value);
            let value = option ? option.description : variable.decimalValue;
            optionTags.push({key: variable.varName, value: value + ' ' + variable.uom});
          } else {
            optionTags.push({key: variable.varName, value: variable.decimalValue + ' ' + variable.uom});
          }
        });
        tags.push(optionTags);
      });

      tags.push(this.globalTags);

      // Remove duplicate tags
      for (let i = 1; i < tags.length; i++) {
        for (let j = 0; j < i; j++) {
          tags[i] = tags[i].filter(tag => { // Filter this array of tags to only include tags where...
            return !tags[j].some(x => x.key == tag.key) // a previous array of tags does not include a tag with the same key
          });
        }
      }

      return tags;
    },
    appliedRadialOptionsTags() {
      let tags = [];

      this.appliedRadialOptions.forEach(option => {
        let optionTags = [];
        option.variables.forEach(variable => {
          if (variable.isDropDown) {
            let option = variable.options.find(x => x.code == variable.value);
            let value = option ? option.description : variable.decimalValue;
            optionTags.push({key: variable.varName, value: value + ' ' + variable.uom});
          } else {
            optionTags.push({key: variable.varName, value: variable.decimalValue + ' ' + variable.uom});
          }
        });
        tags.push(optionTags);
      });

      // Remove duplicate tags
      for (let i = 1; i < tags.length; i++) {
        for (let j = 0; j < i; j++) {
          tags[i] = tags[i].filter(tag => { // Filter this array of tags to only include tags where...
            return !tags[j].some(x => x.key == tag.key) // a previous array of tags does not include a tag with the same key
          });
        }
      }

      return tags;
    },
    appliedRightOptionsTags() {
      let tags = [];

      this.appliedRightOptions.forEach(option => {
        let optionTags = [];
        option.variables.forEach(variable => {
          if (variable.isDropDown) {
            let option = variable.options.find(x => x.code == variable.value);
            let value = option ? option.description : variable.decimalValue;
            optionTags.push({key: variable.varName, value: value + ' ' + variable.uom});
          } else {
            optionTags.push({key: variable.varName, value: variable.decimalValue + ' ' + variable.uom});
          }
        });
        tags.push(optionTags);
      });

      // Remove duplicate tags
      for (let i = 1; i < tags.length; i++) {
        for (let j = 0; j < i; j++) {
          tags[i] = tags[i].filter(tag => { // Filter this array of tags to only include tags where...
            return !tags[j].some(x => x.key == tag.key) // a previous array of tags does not include a tag with the same key
          });
        }
      }

      return tags;
    },

    machiningOptionModalHeader() { return this.selectedOption ? this.selectedOption.displayName : '' },
    machiningOptionVariableHelpModalHeader() { return this.helpModalVariable ? this.helpModalVariable.displayName : '' },

    partnumberHasChanged() { return this.partnumber != this.initialPartnumber; },
    
  },
  methods: {
    init(partnumber) {
      if (partnumber) {
        this.closeMachiningOptionModal();
        this.selectedSection = "Left";
        this.parsedPartnumberOutput = null;
        this.initialPartnumber = partnumber;
        this.partnumber = partnumber;
        this.parsePartnumber();
      }
    },

    updateInfoBoxesContainerHeight() {
      this.$nextTick(function () {
        let actualHeight = this.parsedPartnumberOutput ? this.$refs.infoBoxesContainer.clientHeight - 10 : 0;
        this.infoBoxesContainerHeight = Math.max(actualHeight, 234);
      });
    },

    machiningSectionClicked(section) {
      if (this.validSections.includes(section)) {
        this.selectedSection = section;
      }
    },

    machiningOptionClicked(option) {
      this.selectedOption = option;
      this.$refs.modalMachiningOption.open();

      this.$store.dispatch("trackEvent", {
        action: "Click - Open Machining Option",
        label: option.name
      })
    },

    editMachiningButtonClicked(area, option) {
      this.selectedSection = area;
      this.selectedOption = option;
      this.$refs.modalMachiningOption.open();
      
      this.$store.dispatch("trackEvent", {
        action: "Click - Edit Machining Option",
        label: option.name
      })
    },
    deleteMachiningButtonClicked(area, option) {
      this.selectedSection = area;
      this.clearSelectedOption();

      option.isApplied = false;
      this.refreshPartnumber();

      this.$store.dispatch("trackEvent", {
        action: "Click - Delete Machining Option",
        label: option.name
      })

    },

    closeMachiningOptionModal() {
      this.$refs.modalMachiningOption.close();
      this.clearSelectedOption();
    },
    clearSelectedOption() {
      this.selectedOption = null;
    },

    refreshPartnumber() {
      // Create a new partnumber using the template and all configured segments
      let newPartnumber = this.parsedPartnumberOutput.partNumberTemplate;
      
      this.parsedPartnumberOutput.machiningDetails.forEach(machiningDetail => {
        machiningDetail.machiningOptions.forEach(machiningOption => {
          if (machiningOption.isApplied) {
            let newSequence = machiningOption.pnSequence;

            machiningOption.variables.forEach(variable => {
              newSequence = newSequence.replace(variable.placeholder, variable.value);
            });

            newPartnumber = newPartnumber.replace(machiningOption.placeholder, newSequence);
          }
        });
      });

      // Replace any remaining placeholders in the template with “X”
      newPartnumber = newPartnumber.replace(/{.+?}/g, 'X');

      this.closeMachiningOptionModal();

      this.partnumber = newPartnumber;
      this.parsePartnumber();
    },

    parsePartnumber() {
      this.showLoader = true;

      tim.parsePartnumber(this.partnumber, this.$root.$i18n.locale, this.globalSettings.apiKey)
        .then(output => {
          if (output.status == 200) {
            this.showTimError = false;
            this.parsedPartnumberOutput = output.data;
            this.updateInfoBoxesContainerHeight();
          } else {
            this.showTimError = true;
            this.parsedPartnumberOutput = null;
          }
          this.showLoader = false;
        });

    },

    submitParserField() {
      this.parsePartnumber();
      this.$store.dispatch("trackEvent", {
        action: "Submit - Parse Partnumber",
        label: this.partnumber
      }) 
    },

    revertToInitialPartnumber() {
      if (this.partnumber != this.initialPartnumber) {
        this.partnumber = this.initialPartnumber;
        this.parsePartnumber();
      }
    },

    resetButtonClicked() {
      this.revertToInitialPartnumber();
      this.$store.dispatch("trackEvent", {
        action: "Click - Reset Configuration",
        label: null
      })       
    },


    cancelButtonClicked() {
      this.revertToInitialPartnumber();

      let event = new Event('splSpecialMachiningCancel');
      document.dispatchEvent(event);

      this.$store.dispatch("trackEvent", {
        action: "Click - Cancel Configuration",
        label: null
      })      
    },
    saveButtonClicked() {
      this.initialPartnumber = this.partnumber;

      let event = new CustomEvent('splSpecialMachiningSave', { detail: this.partnumber });
      document.dispatchEvent(event);

      this.$store.dispatch("trackEvent", {
        action: "Click - Save Configuration",
        label: this.partnumber
      })
    },


    openHelpModal(variableVarName) {
      this.helpModalVariable = this.selectedOption.variables.find(x => x.varName == variableVarName);
      this.$refs.modalMachiningOptionVariableHelp.open();
    },
  },
}
</script>

<style lang="scss">
@font-face {
  font-family: "ProximaNova";
  src: url("https://www.thomsonlinear.com/fonts/ProximaNova-Regular.eot");
  src: url("https://www.thomsonlinear.com/fonts/ProximaNova-Regular.eot?#iefix") format("embedded-opentype"), url("https://www.thomsonlinear.com/fonts/ProximaNova-Regular.woff") format("woff"), url("https://www.thomsonlinear.com/fonts/ProximaNova-Regular.ttf") format("truetype"), url("https://www.thomsonlinear.com/fonts/ProximaNova-Regular.svg#ProximaNova-Regular") format("svg");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "ProximaNova";
  src: url("https://www.thomsonlinear.com/fonts/ProximaNova-Bold.eot");
  src: url("https://www.thomsonlinear.com/fonts/ProximaNova-Bold.eot?#iefix") format("embedded-opentype"), url("https://www.thomsonlinear.com/fonts/ProximaNova-Bold.woff") format("woff"), url("https://www.thomsonlinear.com/fonts/ProximaNova-Bold.ttf") format("truetype"), url("https://www.thomsonlinear.com/fonts/ProximaNova-Bold.svg#ProximaNova-Bold") format("svg");
  font-weight: 700;
  font-style: normal
}
.thomsonspecialmachining-spl {
  @import "./css/app.scss";
  @import "./css/button.scss";

  position: relative;
  width: 100%;
  margin: 0 auto;
  background-color: white;

  .machining-option-modal .sweet-title h2,
  .machining-option-variable-help-modal .sweet-title h2 {
    text-align: center;
    line-height: 64px; // Added to override TLO
  }
  .machining-option-modal .sweet-title {
    border-bottom: none;
  }
  .machining-option-modal .sweet-content {
    padding: 0 20px 20px;
  }
}
</style>

<style scoped lang="scss">
.thomson-app {

  .machining-options-container {
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 15px;
  }
  .machining-options__header-container {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 5px;
  }
  .machining-options__header {
    display: inline-block;
    font-size: 18px;
    font-weight: bold;
    text-transform: uppercase;
  }
  .machining-options-container--left,
  .machining-details__image-color-background--left.machining-details__image-color-background--active {
    background-color: rgb(232, 241, 221, 0.6);
  }
  .machining-options-container--left .machining-options__header {
    color: #7BB142;
  }
  .machining-options-container--radial,
  .machining-details__image-color-background--radial.machining-details__image-color-background--active {
    background-color: #dfeaf5;
  }
  .machining-options-container--radial .machining-options__header {
    color: #1F427D;
  }
  .machining-options-container--right,
  .machining-details__image-color-background--right.machining-details__image-color-background--active {
    background-color: rgba(249, 229, 215, 0.6);
  }
  .machining-options-container--right .machining-options__header {
    color: #E27C37;
  }
  .machining-options__header-tagline {
    color: #CC6600;
    font-size: 13px;
  }
  .machining-options__header-tagline a {
    color: #CC6600;
  }
  .machining-options {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -5px -10px;
  }
  .machining-option {
    display: flex;
    flex-direction: column;
    border: 1px solid #797979;
    background-color: white;
    margin: 0 5px 10px;
    padding: 5px;
    width: 120px;
    height: 120px;
    cursor: pointer;
  }
  .machining-option:hover:not(.machining-option--disabled) {
    box-shadow: 0px 0px 11px #00000059;
  }
  .machining-option__image {
    display: block;
    margin: 10px auto 5px;
    height: 33px;
    width: auto;
  }
  .machining-option__name {
    font-size: 12px;
    line-height: 1.2;
    text-align: center;
    margin: auto;
  }
  .machining-option--applied {
    background-color: #1f427d;
    color: white;
  }
  .machining-option--applied img {
    filter: invert(100%);
    -webkit-filter: invert(100%);
  }
  .machining-option--disabled {
    background-color: #d9d9d9;
    border-color: #979797;
    color: #979797;
    cursor: default;
  }
  .machining-option--disabled .machining-option__image {
    opacity: 0.6;
  }

  .machining-details {
    margin-bottom: 15px;
  }
  .machining-tabs {
    display: flex;
    border-bottom: 2px solid #464647;
  }
  .machining-tab {
    flex: 1 0;
    display: flex;
    padding: 12px;
    margin-bottom: -2px;
    background-color: #464647;
    color: white;
    font-size: 13px;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 3px;
    cursor: pointer;
  }
  .machining-tab:not(:last-of-type) {
    margin-right: 5px;
  }
  .machining-tab:not(:first-of-type) {
    margin-left: 5px;
  }
  .machining-tab span {
    margin: auto;
  }
  .machining-tab--left:hover,
  .machining-tab--left.machining-tab--selected {
    background-color: #7bb142;
  }
  .machining-tab--radial:hover,
  .machining-tab--radial.machining-tab--selected {
    background-color: #1f427d;
  }
  .machining-tab--right:hover,
  .machining-tab--right.machining-tab--selected {
    background-color: #e27c37;
  }
  .machining-tab--disabled {
    cursor: default;
    opacity: 0;
  }
  
  .machining-details__image-container {
    position: relative;
  }
  .machining-details__images img {
    z-index: 2;
  }
  .machining-details__images img:first-of-type {
    position: relative;
  }
  .machining-details__images img:not(:first-of-type) {
    position: absolute;
    top: 0;
    left: 0;
  }
  
  .machining-details__image-color-backgrounds,
  .machining-details__image-touch-targets,
  .machining-details__info-boxes-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
  }
  .machining-details__configured-option-tags-container {
    position: relative;
    display: flex;
  }
  .machining-details__image-color-background,
  .machining-details__image-touch-target,
  .machining-details__info-boxes,
  .machining-details__configured-option-tags {
    width: 100%;
  }
  .machining-details__image-color-background:not(:last-of-type),
  .machining-details__image-touch-target:not(:last-of-type),
  .machining-details__info-boxes:not(:last-of-type),
  .machining-details__configured-option-tags:not(:last-of-type) {
    margin-right: 5px;
  }
  .machining-details__image-color-background:not(:first-of-type),
  .machining-details__image-touch-target:not(:first-of-type),
  .machining-details__info-boxes:not(:first-of-type),
  .machining-details__configured-option-tags:not(:first-of-type) {
    margin-left: 5px;
  }

  .machining-details__image-touch-target {
    z-index: 3;
    cursor: pointer;
  }
  .machining-details__image-touch-target--disabled {
    cursor: default;
  }

  .machining-details__info-boxes-container {
    height: auto;
  }
  .machining-details__info-box {
    position: relative;
    z-index: 4;
    background: white;
    border: 1px solid #797979;
    box-shadow: 0px 0px 11px #00000059;
    margin: 10px;
  }
  .machining-details__info-box-title {
    text-align: center;
    display: block;
    margin: 5px 10px 7px;
    font-size: 13px;
    color: #1E1E1E;
  }
  .machining-details__info-box-details {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 0 5px;
  }
  .machining-details__info-box-image-container {
    display: flex;
    flex-grow: 1;
    width: 92px;
    height: 28px;
    margin-bottom: 10px;
  }
  .machining-details__info-box-image {
    height: 28px;
    width: auto;
    margin: auto;
  }
  .machining-details__info-box-buttons {
    flex-wrap: wrap;
    justify-content: center;
    margin: auto;
  }
  .machining-details__info-box-buttons .button {
    width: 65px;
    margin: 0 5px 10px;
  }

  .machining-details__image-tagline {
    position: relative;
    padding: 0 10px 10px;
    font-size: 13px;
    color: #666766;
  }

  .machining-details__configured-option-tags {
    padding: 0 10px 10px;
  }
  .machining-details__configured-option-tag {
    display: inline-block;
    margin-right: 8px;
    font-size: 14px;
    font-weight: 400;
    color: #333333;
  }
  

  .footer {
    display: flex;
  }
  .footer p {
    font-size: 13px;
    color: #666766;
  }
  .footer .buttons {
    width: 100%;
    justify-content: center;
  }
  .footer .button {
    max-width: 140px;
  }



  .flip-image {
    -webkit-transform: scaleX(-1);
    transform: scaleX(-1);
  }

  
  .overlay {
    position: absolute; /* Sit on top of the page content */
    width: 100%; /* Full width (cover the whole page) */
    height: 100%; /* Full height (cover the whole page) */
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255,255,255,0.9); /* Black background with opacity */
    z-index: 10; /* Specify a stack order in case you're using a different order for other elements */
  }
  .initial-spinner {
    margin-left: calc(50% - 30px);
    margin-top: 50px;
    margin-bottom: 50px;
    z-index: 3;
  }
  .loading-spinner {
    position: absolute;
    left: calc(50% - 30px);
    top: calc(50% - 30px);
    z-index: 11;
  }

  .tooltip-popup__container {
    display: flex;
  }
  #service-error-popup {
    position: relative;
    margin: auto;
  }

}
</style>